import React, { useState } from 'react';
import { Search, Download, Trash2, AlertTriangle, FileText, User, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSarExport = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('email');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [exportData, setExportData] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [erasing, setErasing] = useState(false);
  const [confirmErase, setConfirmErase] = useState(null);
  const [error, setError] = useState('');
  const token = localStorage.getItem('uf_token');

  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const searchMembers = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    setError('');
    setExportData(null);
    try {
      const params = new URLSearchParams({ [searchType]: searchQuery.trim() });
      const res = await fetch(`${API_URL}/api/sar/search?${params}`, { headers });
      if (!res.ok) throw new Error((await res.json()).detail || 'Search failed');
      const data = await res.json();
      setResults(data.items || []);
      if ((data.items || []).length === 0) setError('No members found matching your search.');
    } catch (e) {
      setError(e.message);
    } finally {
      setSearching(false);
    }
  };

  const exportMemberData = async (memberId) => {
    setExporting(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/sar/member/${memberId}`, { headers });
      if (!res.ok) throw new Error((await res.json()).detail || 'Export failed');
      const data = await res.json();
      setExportData(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setExporting(false);
    }
  };

  const downloadJson = () => {
    if (!exportData) return;
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sar-export-${exportData.export_metadata?.member_id || 'unknown'}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const eraseMemberData = async (memberId) => {
    setErasing(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/sar/member/${memberId}/erase`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error((await res.json()).detail || 'Erasure failed');
      setConfirmErase(null);
      setExportData(null);
      setResults(prev => prev.map(m => m.id === memberId ? { ...m, status: 'erased' } : m));
    } catch (e) {
      setError(e.message);
    } finally {
      setErasing(false);
    }
  };

  const SectionCard = ({ title, data }) => {
    if (!data || (Array.isArray(data) && data.length === 0)) {
      return (
        <div className="bg-uf-surface border border-uf-border rounded-lg p-4">
          <h4 className="text-sm font-medium text-uf-text mb-2">{title}</h4>
          <p className="text-xs text-uf-text-muted">No data</p>
        </div>
      );
    }
    const items = Array.isArray(data) ? data : [data];
    return (
      <div className="bg-uf-surface border border-uf-border rounded-lg p-4">
        <h4 className="text-sm font-medium text-uf-text mb-3">{title} ({items.length})</h4>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {items.map((item, i) => (
            <div key={i} className="text-xs bg-uf-bg rounded p-2 border border-uf-border/50">
              {Object.entries(item).filter(([, v]) => v !== null && v !== undefined).map(([k, v]) => (
                <div key={k} className="flex justify-between py-0.5">
                  <span className="text-uf-text-muted">{k.replace(/_/g, ' ')}:</span>
                  <span className="text-uf-text ml-2 text-right">{String(v)}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="sar-export-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-uf-text flex items-center gap-2">
          <FileText className="w-6 h-6 text-uf-red" />
          GDPR / Subject Access Request
        </h1>
        <p className="text-sm text-uf-text-muted mt-1">
          Search for a member and export or erase their personal data in compliance with GDPR.
        </p>
      </div>

      {/* Search */}
      <div className="bg-uf-surface border border-uf-border rounded-lg p-4 mb-6" data-testid="sar-search-section">
        <div className="flex flex-col sm:flex-row gap-3">
          <select
            value={searchType}
            onChange={e => setSearchType(e.target.value)}
            style={{ width: 'auto', minWidth: '160px' }}
            className="bg-uf-bg border border-uf-border rounded-lg px-3 py-2 text-sm text-uf-text"
            data-testid="sar-search-type"
          >
            <option value="email">Email</option>
            <option value="name">Name</option>
            <option value="member_number">Member Number</option>
          </select>
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && searchMembers()}
            placeholder={`Search by ${searchType}...`}
            style={{ background: 'var(--uf-bg, #0a0a0a)', border: '1px solid var(--uf-border, #2a2a2a)', borderRadius: '0.5rem', padding: '0.5rem 0.75rem' }}
            className="flex-1 text-sm text-uf-text placeholder:text-uf-text-muted"
            data-testid="sar-search-input"
          />
          <button
            onClick={searchMembers}
            disabled={searching || !searchQuery.trim()}
            className="flex items-center gap-2 bg-uf-red text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-uf-red/90 disabled:opacity-50"
            data-testid="sar-search-btn"
          >
            {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            Search
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4 text-red-400 text-sm" data-testid="sar-error">
          {error}
        </div>
      )}

      {/* Results */}
      {results.length > 0 && (
        <div className="bg-uf-surface border border-uf-border rounded-lg mb-6" data-testid="sar-results">
          <div className="p-4 border-b border-uf-border">
            <h3 className="text-sm font-medium text-uf-text">Search Results ({results.length})</h3>
          </div>
          <div className="divide-y divide-uf-border">
            {results.map(m => (
              <div key={m.id} className="p-4 flex items-center justify-between" data-testid={`sar-result-${m.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-uf-red/20 rounded-full flex items-center justify-center">
                    <User className="w-4 h-4 text-uf-red" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-uf-text">{m.first_name} {m.last_name}</p>
                    <p className="text-xs text-uf-text-muted">{m.email} | {m.member_number || 'N/A'}</p>
                  </div>
                  {m.status === 'erased' && (
                    <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded">ERASED</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => exportMemberData(m.id)}
                    disabled={exporting}
                    className="flex items-center gap-1.5 bg-blue-600/20 text-blue-400 px-3 py-1.5 rounded text-xs font-medium hover:bg-blue-600/30 disabled:opacity-50"
                    data-testid={`sar-export-btn-${m.id}`}
                  >
                    {exporting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                    Export
                  </button>
                  <button
                    onClick={() => setConfirmErase(m.id)}
                    disabled={m.status === 'erased'}
                    className="flex items-center gap-1.5 bg-red-600/20 text-red-400 px-3 py-1.5 rounded text-xs font-medium hover:bg-red-600/30 disabled:opacity-50"
                    data-testid={`sar-erase-btn-${m.id}`}
                  >
                    <Trash2 className="w-3 h-3" /> Erase
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Erase Confirmation Modal */}
      {confirmErase && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" data-testid="sar-erase-modal">
          <div className="bg-uf-surface border border-uf-border rounded-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <h3 className="text-lg font-bold text-uf-text">Confirm Data Erasure</h3>
            </div>
            <p className="text-sm text-uf-text-muted mb-4">
              This will permanently anonymise all personal data for this member. Financial records will be retained per legal obligations. This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setConfirmErase(null)}
                className="px-4 py-2 text-sm border border-uf-border rounded-lg text-uf-text hover:bg-uf-surface-hi"
                data-testid="sar-erase-cancel"
              >
                Cancel
              </button>
              <button
                onClick={() => eraseMemberData(confirmErase)}
                disabled={erasing}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                data-testid="sar-erase-confirm"
              >
                {erasing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Erase Data
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Data Display */}
      {exportData && (
        <div data-testid="sar-export-data">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-uf-text">Exported Data</h3>
            <button
              onClick={downloadJson}
              className="flex items-center gap-2 bg-uf-red text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-uf-red/90"
              data-testid="sar-download-btn"
            >
              <Download className="w-4 h-4" /> Download JSON
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SectionCard title="Personal Data" data={exportData.personal_data} />
            <SectionCard title="Subscriptions" data={exportData.subscriptions} />
            <SectionCard title="Payment History" data={exportData.payment_history} />
            <SectionCard title="Access Logs" data={exportData.access_logs} />
            <SectionCard title="Orders" data={exportData.orders} />
            <SectionCard title="Day Passes" data={exportData.day_passes} />
            <SectionCard title="Agreements" data={exportData.agreements} />
            <SectionCard title="Applications" data={exportData.applications} />
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSarExport;
